/**
 * Settings Context
 *
 * Provides global access to user settings with optimistic updates.
 * Fetches settings on mount and syncs changes with backend.
 */

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useSession } from '@clerk/clerk-react';
import { fetchWithAuth } from 'shared/api/fetch-with-auth';
import { API_URL } from 'constants/index';
import { UserSettings, RenovationCardSettings, DEFAULT_SETTINGS } from 'shared/types/settings';
import { toast } from 'react-toastify';

interface SettingsContextType {
  settings: UserSettings;
  isLoading: boolean;
  getRenovationCardSettings: () => RenovationCardSettings;
  updateRenovationCardSettings: (updates: Partial<RenovationCardSettings>) => Promise<void>;
  resetSettings: () => Promise<void>;
  refetchSettings: () => Promise<void>;
}

const SettingsContext = createContext<SettingsContextType | undefined>(undefined);

export const SettingsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { session } = useSession();
  const [settings, setSettings] = useState<UserSettings>(DEFAULT_SETTINGS);
  const [isLoading, setIsLoading] = useState(true);

  /**
   * Fetch settings from backend
   */
  const fetchSettings = useCallback(async () => {
    if (!session) {
      setIsLoading(false);
      return;
    }

    try {
      const response = await fetchWithAuth(
        `${API_URL}/user-settings`,
        { method: 'GET' },
        session
      );

      if (response.ok) {
        const data = await response.json();
        setSettings(data.settings || DEFAULT_SETTINGS);
      } else {
        console.error('Failed to fetch settings');
        setSettings(DEFAULT_SETTINGS);
      }
    } catch (error) {
      console.error('Error fetching settings:', error);
      setSettings(DEFAULT_SETTINGS);
    } finally {
      setIsLoading(false);
    }
  }, [session]);

  /**
   * Fetch settings on mount
   */
  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  /**
   * Get renovation card settings
   */
  const getRenovationCardSettings = useCallback((): RenovationCardSettings => {
    return settings.renovation?.cardView || DEFAULT_SETTINGS.renovation!.cardView!;
  }, [settings]);

  /**
   * Update renovation card settings with optimistic updates
   */
  const updateRenovationCardSettings = useCallback(
    async (updates: Partial<RenovationCardSettings>) => {
      if (!session) {
        toast.error('Sessie verlopen. Refresh de pagina.');
        return;
      }

      // Store previous settings for rollback
      const previousSettings = { ...settings };

      try {
        // OPTIMISTIC UPDATE: Update local state immediately
        setSettings((prev) => {
          const currentCardView = prev.renovation?.cardView || DEFAULT_SETTINGS.renovation!.cardView!;

          return {
            ...prev,
            renovation: {
              ...prev.renovation,
              cardView: {
                ...currentCardView,
                ...updates,
                // Properly merge visibleSections if provided
                visibleSections: updates.visibleSections
                  ? {
                      ...currentCardView.visibleSections,
                      ...updates.visibleSections,
                    }
                  : currentCardView.visibleSections,
              } as RenovationCardSettings,
            },
          };
        });

        // Make API call in background
        const response = await fetchWithAuth(
          `${API_URL}/user-settings/renovation/card-view`,
          {
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(updates),
          },
          session
        );

        if (!response.ok) {
          throw new Error('Failed to update settings');
        }

        const updatedData = await response.json();
        setSettings(updatedData.settings || DEFAULT_SETTINGS);
      } catch (error: any) {
        console.error('Error updating renovation card settings:', error);
        toast.error('Fout bij opslaan instellingen');

        // ROLLBACK: Revert to previous settings on error
        setSettings(previousSettings);
      }
    },
    [session, settings]
  );

  /**
   * Reset all settings to defaults
   */
  const resetSettings = useCallback(async () => {
    if (!session) {
      toast.error('Sessie verlopen. Refresh de pagina.');
      return;
    }

    try {
      const response = await fetchWithAuth(
        `${API_URL}/user-settings/reset`,
        {
          method: 'PATCH',
        },
        session
      );

      if (!response.ok) {
        throw new Error('Failed to reset settings');
      }

      const data = await response.json();
      setSettings(data.settings || DEFAULT_SETTINGS);
      toast.success('Instellingen gereset naar standaard');
    } catch (error: any) {
      console.error('Error resetting settings:', error);
      toast.error('Fout bij resetten instellingen');
    }
  }, [session]);

  /**
   * Refetch settings from backend
   */
  const refetchSettings = useCallback(async () => {
    await fetchSettings();
  }, [fetchSettings]);

  const value: SettingsContextType = {
    settings,
    isLoading,
    getRenovationCardSettings,
    updateRenovationCardSettings,
    resetSettings,
    refetchSettings,
  };

  return <SettingsContext.Provider value={value}>{children}</SettingsContext.Provider>;
};

/**
 * Hook to access settings context
 */
export const useSettings = (): SettingsContextType => {
  const context = useContext(SettingsContext);
  if (!context) {
    throw new Error('useSettings must be used within a SettingsProvider');
  }
  return context;
};
